import { useState, useContext, useEffect, useRef } from "react";
import { Tooltip } from "../Tooltip";
import { useHistory, useLocation } from "react-router-dom";
import SimpleBackIcon from "../../assets/simpleBackIcon.svg";
import { AuthContext } from "../../contexts/auth-context";
import InfoPopup from "../InfoPopup";
import { useAsyncFn } from "../../hooks/use-async-fn";
import SearchIcon from "../../assets/search.svg";
import styles from "./nestedQueries.module.css";
import PlusIcon from "../../assets/plusIcon.svg";
import DoneIcon from "../../assets/done.svg";
import LayoutContainer from "../LayoutContainer";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import LimitsUsageBanner from "../LimitsUsageBanner";
import {
  useLimitsUsage,
  QueryTypeUsage,
} from "../../hooks/use-limits-usage";
import { getQueryWarningMessage } from "../../utils";
import { useToast } from "../../contexts/toast-context";

const TriggerNestedQueries = () => {
  const { putCompoundQuery, user, getCampaignNames, applicationId } =
    useContext(AuthContext);
  const location = useLocation();
  const [loading, showLoader] = useState(false);
  const queries = location.state?.selectedItems || [];
  const { setBreadcrumbItems } = useBreadcrumb();
  const [selectedData, setSelectedData] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [showErrorMessage, setShowErrorMessage] = useState<string | boolean>(
    false,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState({ id: "", name: "" });
  const [isCampaignAdded, setIsCampaignAdded] = useState(false);
  const [, setShowTickIcon] = useState(false);
  const [campaignNames, setCampaignNames] = useState([]);
  const [showPlusIcon, setShowPlusIcon] = useState(false);
  const [description, setDescription] = useState("");
  const [campaignNameError, setCampaignNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();

  const { given_name, family_name } = user || {};
  var user_name = given_name + " " + family_name;
  const history = useHistory();
  var sessionFederationId = localStorage.getItem("federationkey");

  var sessionOrganizationId = localStorage.getItem("organizationkey");

  const bucketIds = JSON.parse(localStorage.getItem("bucketids") || "[]");
  var orgId = sessionOrganizationId;

  const putCompoundQueryFn = useAsyncFn({
    deferFn: putCompoundQuery,
  });

  const getCampaignNamesFn = useAsyncFn({
    deferFn: getCampaignNames,
  });

  // All combined queries must share one objective (see the compound query
  // payload below - there's only one contributor_bucket/filters set), so the
  // first selected query's objective represents the whole batch.
  const nestedObjective = queries[0]?.objective;
  const { fetchUsage: fetchLimitsUsageData, loading: limitsUsageLoading } =
    useLimitsUsage();
  const [objectiveUsage, setObjectiveUsage] = useState<
    QueryTypeUsage | undefined
  >(undefined);

  useEffect(() => {
    if (!nestedObjective || !sessionFederationId || !orgId) {
      setObjectiveUsage(undefined);
      return;
    }
    let cancelled = false;
    fetchLimitsUsageData([nestedObjective], {
      organizationId: orgId,
      federationId: sessionFederationId,
    }).then((results) => {
      if (!cancelled) setObjectiveUsage(results[0]?.usage);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nestedObjective, sessionFederationId, orgId]);


  const fedSpecificMenuLabels = JSON.parse(
    localStorage.getItem("fedSpecificMenuLabels") || "{}",
  );
  useEffect(() => {
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Federation Selection", path: "/federation-selection" },
      {
        label:
          `${fedSpecificMenuLabels?.campaign_reports}` || `Audience Details`,
        path: "/campaign-reports",
      },
      { label: "Nested Queries", path: "/nested-queries" },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const result = await getCampaignNamesFn.run({ orgId });
        setCampaignNames(
          result?.Items.map((item) => ({ id: item.id, name: item.name })),
        );
      } catch (error) {
        console.error("Error fetching campaign data:", error);
      }
    };

    fetchCampaignData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownContainerRef.current &&
        !dropdownContainerRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setIsCampaignAdded(false);
    setShowTickIcon(false);
    if (
      campaignNames &&
      !campaignNames.some(
        (campaign) => campaign.name.toLowerCase() === val.toLowerCase(),
      )
    ) {
      setShowPlusIcon(true);
    } else {
      setShowPlusIcon(false);
    }
    if (val.length > 40) {
      setInputValue({ id: "", name: val.slice(0, 40) });
      setCampaignNameError("Cohort name cannot exceed 40 characters.");
    } else {
      setInputValue({ id: "", name: val });
      setCampaignNameError("");
    }
    setIsDropdownOpen(val.length > 0);
  };

  const handleDescriptionChange = (e) => {
    const val = e.target.value;
    if (val.length > 100) {
      setDescription(val.slice(0, 100));
      setDescriptionError("Description cannot exceed 100 characters.");
    } else {
      setDescription(val);
      setDescriptionError("");
    }
  };

  const handleSelect = (campaign) => {
    setShowTickIcon(true);
    setShowPlusIcon(false);
    setInputValue({ id: campaign.id, name: campaign.name });
    setIsDropdownOpen(false);
  };

  // const queries = [
  //   {
  //     query_id: "xyz 123",
  //     filter_str: ["credit_ratings = 'Medium'"],
  //     result_set_components: [
  //       { org_ids: "Organization 1", count: 3000 },
  //       { org_ids: "Organization 2", count: 5500 },
  //       { org_ids: "Organization 3", count: 15500 },
  //     ],
  //   },
  //   {
  //     query_id: "xyz 1235",
  //     filter_str: ["credit_ratings = 'Medium'"],
  //     result_set_components: [
  //       { org_ids: "Organization 1", count: 3000 },
  //       { org_ids: "Organization 2", count: 5500 },
  //       { org_ids: "Organization 3", count: 15500 },
  //     ],
  //   },
  //   {
  //     query_id: "xyz 1233",
  //     filter_str: ["credit_ratings = 'Medium'", "credit_ratings = 'Medium'"],
  //     result_set_components: [
  //       { org_ids: "Organization 1", count: 3000 },
  //       { org_ids: "Organization 2", count: 5500 },
  //       { org_ids: "Organization 3", count: 15500 },
  //     ],
  //   },
  // ];

  const handleChange = (queryId, org, index, checked) => {
    const key = `${queryId}_${org.org_ids}`;
    const op = selectedData[key]?.logical_operator;
    setSelectedData((prev) => {
      const updated = { ...prev };
      if (checked) {
        updated[key] = {
          query_id: queryId,
          org_ids: org.org_ids,
          count: org.count,
          logical_operator: op === "OR" || op === "AND" ? op : "OR",
          result_location: org.result_location,
        };
      } else {
        delete updated[key];
      }
      return updated;
    });
  };

  const handleDropdownChange = (queryId, org, field, value) => {
    const key = `${queryId}_${org.org_ids}`;
    setSelectedData((prev) => {
      const exists = prev[key];
      const updated = { ...prev };

      if (exists) {
        updated[key] = {
          ...updated[key],
          [field]: value,
        };
      } else {
        updated[key] = {
          query_id: queryId,
          org_ids: org.org_ids,
          count: org.count,
          logical_operator: value,
          result_location: org.result_location,
        };
      }

      return updated;
    });

    const itemKey = `${queryId}_${org.org_ids}`;
    setSelectedItems((prev) =>
      prev.includes(itemKey) ? prev : [...prev, itemKey],
    );
  };

  const selectedQueryIds = new Set(
    Object.values(selectedData).map((item: { query_id?: string }) => item.query_id),
  );
  const hasMinimumQueries = selectedQueryIds.size >= 2;

  // FR-20 Task 1 AC8: block submission proactively once the org has used up
  // its query quota for this objective, instead of only surfacing the
  // backend's rejection after a wasted round trip. There's no single fixed
  // set of physical tables here (the compound query spans whichever
  // buckets its combined queries touch), so unlike NewQuery this can only
  // gate on the org-level limit, not per-table ones.
  const orgQueryLimitReached =
    !!objectiveUsage?.query_limit_usage &&
    objectiveUsage.query_limit_usage.query_count >=
      objectiveUsage.query_limit_usage.query_limit;

  const handleProceed = async () => {
    if (!hasMinimumQueries || inputValue.name.trim() === "" || orgQueryLimitReached) {
      return;
    }

    const compound_query_input = Object.values(selectedData);
    const finalOutput = {
      is_compound_query: true,
      compound_query_input,
    };

    showLoader(true);
    try {
      const body = {
        user_name: user_name,
        federation_id: sessionFederationId,
        identifier: queries[0]?.identifier,
        inquirer_organization_id: orgId,
        inquirer_bucket_id: "",
        contributor_bucket: bucketIds,
        attributes_selected: [],
        campaign_name: inputValue?.name,
        description,
        ...finalOutput,
      };

      const result = await putCompoundQueryFn.run({
        body,
        fedId: sessionFederationId,
        orgId,
        applicationId,
      });

      const warningMessage = getQueryWarningMessage(result);
      if (warningMessage) showToast(warningMessage, "warning");

      setTimeout(() => {
        showLoader(false);
        history.push("/campaign-reports");
      }, 1000);
    } catch (error: any) {
      console.error("Error invoking query:", error);
      showLoader(false);
      setShowErrorMessage(error?.message || true);
    }
  };

  const goBack = () => {
    window?.history?.back();
  };

  const campaignNameQuery = (inputValue?.name || "").toLowerCase();
  const filteredCampaignNames =
    campaignNames?.filter((campaign) =>
      campaign.name.toLowerCase().includes(campaignNameQuery),
    ) ?? [];

  const LoadingSpinner = () => (
    <div className={styles.spinnerOverlay}>
      <div className={styles.spinner}></div>
    </div>
  );

  return (
    <LayoutContainer>
      {loading && <LoadingSpinner />}
      {showErrorMessage && (
        <InfoPopup
          headerText="Information!"
          displayText={
            typeof showErrorMessage === "string"
              ? showErrorMessage
              : "Something went wrong. Please try again!"
          }
          freeText
          isOpen={!!showErrorMessage}
          onClose={() => setShowErrorMessage(false)}
        />
      )}
      <div className={styles.container}>
        <div className={styles.actionsBar}>
          <button className={styles.backButton} onClick={goBack}>
            <img src={SimpleBackIcon} alt="back" />
            <span className={styles.backButtonText}>Back</span>
          </button>
        </div>
        <LimitsUsageBanner loading={limitsUsageLoading} usage={objectiveUsage} />
        <div className={styles.queriesGrid}>
        {queries?.map((query, qIndex) => (
          <div key={qIndex} className={styles.queryBlock}>
            <div className={styles.queryMeta}>
              <p className={styles.queryMetaText}>
                <strong>Query ID:</strong>{" "}
                <span className={styles.queryMetaValue}>{query.id}</span>
              </p>
              <p className={styles.queryMetaText}>
                <strong>Filters:</strong>{" "}
                <span className={styles.queryMetaValue}>
                  {Array.from(
                    new Set(
                      (query?.filter_str || []).filter(
                        (str: unknown): str is string =>
                          typeof str === "string" && str.trim() !== "",
                      ),
                    ),
                  )
                    .map((str) => `"${str}"`)
                    .join(", ")}
                </span>
              </p>
            </div>

            {query?.result_set_components ? (() => {
              const visibleRows = query.result_set_components
                .filter((org) => org.count > 0)
                .filter(
                  (org, i, arr) =>
                    arr.findIndex((o) => o.org_names === org.org_names) === i,
                );
              return visibleRows.length === 0 ? (
                <i>No records to display — all entries have a count of zero.</i>
              ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th></th>
                    <th>Organization</th>
                    <th>Count</th>
                    <th>Logical Operator</th>
                    {/* <th>Not Operator</th> */}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((org, i) => {
                    return (
                      <tr key={i}>
                        <td>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              const itemKey = `${query.id}_${org.org_ids}`; // ✅ Corrected key

                              setSelectedItems((prev) =>
                                isChecked
                                  ? [...prev, itemKey]
                                  : prev?.filter((item) => item !== itemKey),
                              );

                              handleChange(query.id, org, i, isChecked);
                            }}
                            checked={selectedItems.includes(
                              `${query.id}_${org.org_ids}`,
                            )} // ✅ Match the same key
                          />
                        </td>
                        <td>{org.org_names}</td>
                        <td>{org.count.toLocaleString("en-US")}</td>
                        <td>
                          <div className={styles.operatorPills}>
                            {[
                              { value: "OR", label: "Union" },
                              { value: "AND", label: "Intersection" },
                            ].map((op) => {
                              const current =
                                selectedData[`${query.id}_${org.org_ids}`]
                                  ?.logical_operator || "OR";
                              return (
                                <button
                                  key={op.value}
                                  className={`${styles.pillBtn}${current === op.value ? ` ${styles.pillBtnActive}` : ""}`}
                                  onClick={() =>
                                    handleDropdownChange(
                                      query.id,
                                      org,
                                      "logical_operator",
                                      op.value,
                                    )
                                  }
                                >
                                  {op.label}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                        {/* <td>
                          <select
                            value={selected.not_operator ? "true" : "false"}
                            onChange={(e) =>
                              handleDropdownChange(
                                query.id,
                                i,
                                "not_operator",
                                e.target.value
                              )
                            }
                          >
                            <option value="false">False</option>
                            <option value="true">True</option>
                          </select>
                        </td> */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              );
            })() : (
              <i>No data found.</i>
            )}
          </div>
        ))}
        </div>

        <div className={styles.sectionDivider}>
          <span className={styles.sectionLabel}>Cohort Brief</span>
        </div>
        <div className={styles.formRowSecond}>
          <div className={styles.nameFieldContainer}>
            <label className={styles.label}>
              Cohort Name <span className={styles.required}>*</span>
            </label>
            <div className={styles.nameFieldWithDropdown} ref={dropdownContainerRef}>
              <div className={styles.nameField}>
                <img
                  src={SearchIcon}
                  alt="Search"
                  className={styles.searchIcon}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                />
                <input
                  type="text"
                  placeholder="Enter Name"
                  value={inputValue?.name}
                  onChange={handleInputChange}
                  maxLength={40}
                />
                {inputValue &&
                  campaignNames &&
                  !campaignNames.some(
                    (campaign) =>
                      campaign.name.toLowerCase() ===
                      inputValue?.name?.toLowerCase(),
                  ) &&
                  showPlusIcon && (
                    <Tooltip text="Create a new cohort" placement="top">
                    <img
                      src={PlusIcon}
                      alt="Add"
                      className={styles.plusIcon}
                      onClick={() => {
                        handleSelect({ id: "", name: inputValue.name });
                        setIsCampaignAdded(true);
                      }}
                    />
                    </Tooltip>
                  )}
                {isCampaignAdded && (
                  <img src={DoneIcon} alt="Done" className={styles.doneIcon} />
                )}
              </div>
              {isDropdownOpen && filteredCampaignNames.length > 0 && (
                <div className={styles.dropdown}>
                  {filteredCampaignNames.map((campaign) => (
                    <div
                      key={campaign.id}
                      className={styles.dropdownItem}
                      onClick={() => handleSelect(campaign)}
                    >
                      {campaign.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.fieldMeta}>
              {campaignNameError ? (
                <span className={styles.errorMessage}>{campaignNameError}</span>
              ) : (
                <span />
              )}
              <span
                className={`${styles.charCount}${(inputValue?.name?.length ?? 0) >= 36 ? ` ${styles.charCountWarn}` : ""}`}
              >
                {inputValue?.name?.length ?? 0} / 40
              </span>
            </div>
          </div>

          <div className={styles.descriptionFieldContainer}>
            <label className={styles.label}>Description</label>
            <div className={styles.descriptionField}>
              <textarea
                rows={1}
                placeholder="What is the goal of this cohort?"
                value={description}
                onChange={handleDescriptionChange}
              />
            </div>
            <div className={styles.fieldMeta}>
              {descriptionError ? (
                <span className={styles.errorMessage}>{descriptionError}</span>
              ) : (
                <span />
              )}
              <span
                className={`${styles.charCount}${description.length >= 90 ? ` ${styles.charCountWarn}` : ""}`}
              >
                {description.length} / 100
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.footerButtons}>
        <button
          className={styles.cancelButton}
          onClick={() => history.push("/campaign-reports")}
        >
          Cancel
        </button>
        <Tooltip
          text={
            orgQueryLimitReached
              ? "Organization has reached its query limit for this query objective. Contact administrator."
              : !hasMinimumQueries && selectedQueryIds.size > 0
                ? "Please select at least 2 queries"
                : undefined
          }
          placement="top"
        >
        <button
          className={styles.proceedButton}
          disabled={!hasMinimumQueries || inputValue.name.trim() === "" || orgQueryLimitReached}
          style={{
            opacity:
              !hasMinimumQueries || inputValue.name.trim() === "" || orgQueryLimitReached
                ? 0.7
                : 1,
            cursor:
              !hasMinimumQueries || inputValue.name.trim() === "" || orgQueryLimitReached
                ? "not-allowed"
                : "pointer",
          }}
          onClick={handleProceed}
        >
          Proceed
        </button>
        </Tooltip>
      </div>
    </LayoutContainer>
  );
};

export default TriggerNestedQueries;
